/* eslint-disable @typescript-eslint/no-explicit-any */
import Rollbar from 'rollbar'
import { environment } from '@mortgage-pos/ui/env'
import newRelic from '@mortgage-pos/ui/services/newRelic'
import { EncryptedSensitive } from '@mortgage-pos/types'
import {
  aggregateNewRelicErrors,
  isBrowserExtensionError,
  isThirdPartyScriptError,
} from '@mortgage-pos/utils'
import CryptoClient from '@mortgage-pos/ui/services/CryptoClient'

import {
  decorateEncryptedPayload,
  stripSensitiveDataFromPayload,
} from '@mortgage-pos/rollbar-crypto'

const { appEnv } = environment

const checkIgnore = (
  isUncaught: boolean,
  args: Rollbar.LogArgument,
  payload: Record<string, any>
): boolean => {
  if (isBrowserExtensionError(payload)) return true
  if (isThirdPartyScriptError(payload)) return true

  // captures bubbled up messages from window.onerror
  if (isUncaught) return false

  if (payload?.level === 'debug') {
    return true
  }

  if (aggregateNewRelicErrors(newRelic, payload)) return true
}

const ignoredMessages = [
  'ChunkLoadError',
  'Loading chunk',
  'Object Not Found Matching Id',
]

const client =
  appEnv !== 'local'
    ? new Rollbar({
        enabled: environment.rollbar.enabled === 'true',
        environment: `${environment.productName}-${environment.rollbar.environment}`,
        accessToken: environment.rollbar.token,
        captureUncaught: true,
        captureUnhandledRejections: true,
        autoInstrument: {
          // Enable all auto-instrumentation features
          log: true,
          dom: true,
          navigation: true,
          connectivity: true,
          network: true,
          // CSP violation reporting
          contentSecurityPolicy: true, // Track CSP violations in telemetry
          errorOnContentSecurityPolicy: true, // Send CSP violations as Rollbar errors
        },
        scrubTelemetryInputs: true,
        itemsPerMinute: 5,
        checkIgnore,
        ignoredMessages,
        payload: {
          context: 'mortgage-pos#alfie-ui',
          client: {
            javascript: {
              source_map_enabled: true,
              // Optionally have Rollbar guess which frames the error was thrown from
              // when the browser does not provide line and column numbers.
              guess_uncaught_frames: true,
              code_version: process.env.CIRCLE_SHA1,
            },
          },
        },
      })
    : (console as any)

if (appEnv !== 'local') {
  client.configure({
    transform: async (payload) => {
      let encryptedSensitive: EncryptedSensitive

      // Only process encryption for items with sensitive data
      if (payload?.custom?.sensitive) {
        try {
          encryptedSensitive = await CryptoClient.encryptSensitiveData(
            payload.custom.sensitive
          )
          newRelic.increment('tumbleweed.encryption_success')
        } catch (error) {
          newRelic.increment('tumbleweed.encryption_failure')
        }

        if (
          encryptedSensitive?.sensitive &&
          encryptedSensitive?.sensitiveUuid
        ) {
          const { sensitiveUuid } = encryptedSensitive
          decorateEncryptedPayload(sensitiveUuid, payload)
        }
      }

      stripSensitiveDataFromPayload(payload)
    },
  })
}

;(window as any).rollbar = client

export default client
